import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { Shell } from './components/layout/Layout';
import { ChatWidget } from './components/ChatWidget';
import ProtectedRoute from './components/ProtectedRoute';
import { ErrorBoundary } from './components/ErrorBoundary';
import { useT } from './i18n';

// Eagerly loaded — Overview is the landing page; loading lazily would
// add a flash on first paint. Settings + ChatHistory are tiny but kept
// lazy below so they don't pull ECharts / d3-geo / KaTeX into the main bundle.
import { Overview } from './pages/Overview';

// Chart-heavy or otherwise large pages — lazy-loaded so that visiting
// chart-free routes (Settings, ChatHistory) doesn't pay the ECharts /
// d3-geo / KaTeX cost. See Phase 4.1.1.
const Members        = lazy(() => import('./pages/Members').then(m => ({ default: m.Members })));
const MemberDetail   = lazy(() => import('./pages/MemberDetail').then(m => ({ default: m.MemberDetail })));
const MemberCompare  = lazy(() => import('./pages/MemberCompare').then(m => ({ default: m.MemberCompare })));
const Sessions       = lazy(() => import('./pages/Sessions').then(m => ({ default: m.Sessions })));
const SessionDetail  = lazy(() => import('./pages/SessionDetail').then(m => ({ default: m.SessionDetail })));
const Bills          = lazy(() => import('./pages/Bills').then(m => ({ default: m.Bills })));
const BillDetail     = lazy(() => import('./pages/BillDetail').then(m => ({ default: m.BillDetail })));
const Parties        = lazy(() => import('./pages/Parties').then(m => ({ default: m.Parties })));
const FactionDetail  = lazy(() => import('./pages/FactionDetail').then(m => ({ default: m.FactionDetail })));
const Debates        = lazy(() => import('./pages/Debates').then(m => ({ default: m.Debates })));
const DebateDetail   = lazy(() => import('./pages/DebateDetail').then(m => ({ default: m.DebateDetail })));
const Votes          = lazy(() => import('./pages/Votes').then(m => ({ default: m.Votes })));
const VoteDetail     = lazy(() => import('./pages/VoteDetail').then(m => ({ default: m.VoteDetail })));
const Committees     = lazy(() => import('./pages/Committees').then(m => ({ default: m.Committees })));
const CommitteeDetail= lazy(() => import('./pages/CommitteeDetail').then(m => ({ default: m.CommitteeDetail })));
const Settings       = lazy(() => import('./pages/Settings').then(m => ({ default: m.Settings })));
const ChatHistory    = lazy(() => import('./pages/ChatHistory').then(m => ({ default: m.ChatHistory })));
const Reports        = lazy(() => import('./pages/Reports').then(m => ({ default: m.Reports })));
const Usage          = lazy(() => import('./pages/Usage').then(m => ({ default: m.Usage })));
const Feedback       = lazy(() => import('./pages/Feedback').then(m => ({ default: m.Feedback })));
const Documentation  = lazy(() => import('./pages/Documentation').then(m => ({ default: m.Documentation })));
const Notifications  = lazy(() => import('./pages/Notifications').then(m => ({ default: m.Notifications })));

// Honours the Settings → Default landing page preference (stored under
// `default_landing` by Settings.tsx). On a cold visit to `/`, redirect
// to the saved path; if none is set, fall through to <Overview />. The
// settings-page server save still persists this across browsers, but the
// redirect itself uses localStorage so first-paint doesn't have to wait
// on the /v1/settings round-trip.
function LandingRedirect() {
  let saved: string | null = null;
  try {
    const raw = localStorage.getItem('default_landing');
    if (raw) saved = JSON.parse(raw);
  } catch { /* malformed JSON → ignore */ }
  if (saved && saved !== '/' && saved.startsWith('/')) {
    return <Navigate to={saved} replace />;
  }
  return <Overview />;
}

function RouteFallback() {
  const t = useT();
  return (
    <div className="flex items-center justify-center min-h-[40vh]">
      <div
        className="w-6 h-6 rounded-full border-2 border-[var(--border)] border-t-[var(--accent)] animate-spin"
        aria-label={t('common.loading', 'Loading')}
      />
    </div>
  );
}

function App() {
  // Drive the page-level ErrorBoundary's auto-reset: navigating to a new route
  // clears a captured error so a crash on one page doesn't strand the user.
  const location = useLocation();
  return (
    <Routes>
      {/* Public — the user manual is reachable without authentication for
          SEO and pre-signup demos. Each <Documentation /> render manages
          its own chrome (sign-in CTA when unauthed, full nav when signed
          in) instead of relying on the protected <Shell>. */}
      <Route path="/documentation/*" element={
        <Suspense fallback={<RouteFallback />}>
          <Documentation />
        </Suspense>
      } />
      <Route path="/documentation" element={
        <Suspense fallback={<RouteFallback />}>
          <Documentation />
        </Suspense>
      } />

      {/* Protected — all other routes require authentication */}
      <Route
        path="*"
        element={
          <ProtectedRoute>
            <Shell>
              {/* Chat is volatile (streaming, SSE, markdown render) — isolate it
                  so a chat crash never takes the page down with it. */}
              <ErrorBoundary fallback={() => null}>
                <ChatWidget />
              </ErrorBoundary>
              {/* Page-level boundary: a crashing route keeps the shell/nav alive
                  and auto-recovers when the user navigates elsewhere. */}
              <ErrorBoundary resetKeys={[location.pathname]}>
                <Suspense fallback={<RouteFallback />}>
                  <Routes>
                  <Route path="/" element={<LandingRedirect />} />
                  <Route path="/members" element={<Members />} />
                  <Route path="/debates" element={<Debates />} />
                  <Route path="/bills" element={<Bills />} />
                  <Route path="/votes" element={<Votes />} />
                  <Route path="/parties" element={<Parties />} />
                  <Route path="/parties/factions/:abbreviation" element={<FactionDetail />} />
                  <Route path="/sessions" element={<Sessions />} />
                  <Route path="/members/compare" element={<MemberCompare />} />
                  <Route path="/members/:id" element={<MemberDetail />} />
                  <Route path="/sessions/:id" element={<SessionDetail />} />
                  <Route path="/bills/:id" element={<BillDetail />} />
                  <Route path="/votes/:id" element={<VoteDetail />} />
                  <Route path="/debates/:id" element={<DebateDetail />} />
                  <Route path="/committees" element={<Committees />} />
                  <Route path="/committees/:number" element={<CommitteeDetail />} />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="/chat-history" element={<ChatHistory />} />
                  <Route path="/chat-history/:sessionId" element={<ChatHistory />} />
                  <Route path="/reports" element={<Reports />} />
                  <Route path="/reports/:reportId" element={<Reports />} />
                  <Route path="/notifications" element={<Notifications />} />
                  {/* Admin-only — gated server-side; non-devs see a 403 / empty state. */}
                  <Route path="/usage" element={<Usage />} />
                  <Route path="/feedback" element={<Feedback />} />
                  </Routes>
                </Suspense>
              </ErrorBoundary>
            </Shell>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

export default App;
